/*
 *
 * @Copyright 2026 UNLOCKIT DECENTRALIZATION, LDA
 *
 */

import {
    FunctionComponent,
    useEffect,
    useRef,
    useState,
} from 'react';
import * as fleckSdk from '@fleck.chat/partner-sdk';
import { useCornerstoneApi } from '../../api';
import { FLECK_SRC } from '../../settings';
import { preparePageTitle } from '../../utils/route';
import { useAppSelector } from '../../utils/storeHooks';
import { LoadingCircles } from '../elements/LoadingCircles';

type FleckAccess = {
    show: boolean;
    use: boolean;
};

type FleckTokenResponse = {
    jwt: string;
};

const RealEstateLegalAdvisorScreen: FunctionComponent = () => {
    const api = useCornerstoneApi();
    const hasOrganization = Boolean(useAppSelector((state) => state.organization.organizationSelected?.organization));
    const containerRef = useRef<HTMLDivElement | null>(null);
    const fleckRef = useRef<Awaited<ReturnType<typeof fleckSdk.init>> | null>(null);
    const [jwt, setJwt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        document.title = preparePageTitle('Veritas');
    }, []);

    useEffect(() => {
        if (!hasOrganization) {
            return;
        }

        let active = true;

        const fetchAccess = async () => {
            try {
                setIsLoading(true);
                const { data } = await api.get<{ access?: FleckAccess }>('/fleck/access');
                if (!active) {
                    return;
                }
                if (!data?.access?.show) {
                    setIsLoading(false);
                    return;
                }
                const tokenResponse = await api.get<FleckTokenResponse>('/fleck/token');
                if (active) {
                    setJwt(tokenResponse.data.jwt);
                }
            } catch {
                if (active) {
                    setIsLoading(false);
                }
            }
        };

        fetchAccess();

        return () => {
            active = false;
        };
    }, [hasOrganization]);

    useEffect(() => {
        if (!jwt) {
            return;
        }

        const src = FLECK_SRC;
        if (!src) {
            setIsLoading(false);
            return;
        }

        if (!containerRef.current) {
            return;
        }

        if (fleckRef.current) {
            fleckRef.current.auth({ jwt });
            setIsLoading(false);
            return;
        }

        let active = true;

        const initSdk = async () => {
            try {
                const fleck = await fleckSdk.init({
                    container: containerRef.current as HTMLDivElement,
                    src,
                });

                if (!active) {
                    fleck.iframe?.remove();
                    return;
                }

                fleckRef.current = fleck;
                await fleck.auth({ jwt });
                setIsLoading(false);
            } catch {
                if (active) {
                    setIsLoading(false);
                }
            }
        };

        initSdk();

        return () => {
            active = false;
            if (fleckRef.current?.iframe) {
                fleckRef.current.iframe.remove();
                fleckRef.current = null;
            }
        };
    }, [jwt]);

    return (
        <div className="real-estate-legal-advisor-screen">
            <div className="real-estate-legal-advisor-screen__canvas" ref={containerRef}>
                {isLoading && <LoadingCircles size="l" variant="neutral" />}
            </div>
        </div>
    );
};

export { RealEstateLegalAdvisorScreen };
